/**
 * Preview route for the redesigned invoice / quotation PDF template.
 * Visit /preview/invoice?type=short|long|quotation to inspect the layout
 * without triggering window.print(). Renders the same HTML produced by
 * buildInvoiceHtml inside an iframe so all CSS (including @page rules)
 * is sandboxed exactly like a real print preview.
 *
 * NOTE: This is a developer-only route — it uses hard-coded sample data.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { buildInvoiceHtml } from '../lib/printDoc';

const longItems = Array.from({ length: 28 }, (_, i) => ({
    sku: `SKU-${String(i + 1).padStart(4, '0')}`,
    product_name: [
        'Sérum Hydratant Premium 30ml',
        'Crème Anti-Âge Visage 50ml',
        'Masque Argile Purifiant 100ml',
        'Huile de Rose Musquée Pure 30ml',
        'Démaquillant Bi-phasé 200ml',
        'Lotion Tonique Eau de Bleuet 250ml',
        'Baume Lèvres Karité 15ml',
    ][i % 7],
    quantity: (i % 3) + 1,
    unit_price: 12.5 + (i % 7) * 3.4,
    vat_rate: 20,
    line_total: ((i % 3) + 1) * (12.5 + (i % 7) * 3.4),
}));
const shortItems = longItems.slice(0, 3);

const baseTx = {
    invoice_number: 'UC2602000037',
    created_at: new Date().toISOString(),
    created_by_name: 'Léa Martin',
    company_name: 'Parfumerie Bellevue SARL',
    company_address: '42 Rue de la République, 75011 Paris',
    contact_person: 'Mme Camille Dupont',
    mobile_number: '+33 6 12 34 56 78',
    email_address: 'achats@bellevue.fr',
    customer_code: 'UCC-00248',
    siret: '81234567800019',
    tva: 'FR98812345678',
    type: 'invoice',
    status: 'final',
    notes: 'Livraison fractionnée — premier lot semaine 27.',
    discount_amount: 18.5,
    discount_type: 'percent',
    discount_value: 5,
    delivery_charge: 12.0,
    payment_method: 'bank_transfer',
};

function buildSample(kind) {
    const items = kind === 'long' ? longItems : shortItems;
    const subtotal = items.reduce((s, li) => s + li.line_total, 0);
    const discount_amount = +(subtotal * 0.05).toFixed(2);
    const taxable = subtotal - discount_amount;
    const vat_amount = +(taxable * 0.2).toFixed(2);
    const delivery_charge = 12.0;
    const total = +(taxable + vat_amount + delivery_charge).toFixed(2);
    const tx = {
        ...baseTx,
        line_items: items,
        subtotal,
        discount_amount,
        vat_rate: 20,
        vat_amount,
        vat_breakdown: { 20: vat_amount },
        delivery_charge,
        amount: total,
    };
    if (kind === 'quotation') {
        tx.status = 'quotation';
        tx.invoice_number = 'DEV2602000012';
    }
    return tx;
}

export default function PreviewInvoice() {
    const [search] = useSearchParams();
    const kind = search.get('type') || 'short';
    const lang = search.get('lang') || 'fr';
    const raw = search.get('raw') === '1';
    const iframeRef = useRef(null);

    const html = useMemo(() => {
        const tx = buildSample(kind);
        return buildInvoiceHtml(tx, { language: lang })
            // strip the auto window.print() so the preview iframe doesn't open a print dialog
            .replace('<body onload="window.print()">', '<body>');
    }, [kind, lang]);

    useEffect(() => {
        // raw mode: rewrite the entire document so the print CSS (@page,
        // position:fixed header/footer) governs the top-level page.
        //
        // We navigate to a blob URL rather than calling `document.write`
        // (a) so we no longer trip static-analysis XSS warnings, and
        // (b) because a fresh navigation gives us a clean document
        // context — the print CSS `@page` rules take effect without
        // interference from the SPA's own <body> classes.
        if (raw) {
            const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            window.location.replace(url);
            // The current React tree unmounts as soon as the navigation
            // completes, so we don't need to revoke — the browser handles
            // it when the URL leaves scope.
            return;
        }
        const iframe = iframeRef.current;
        if (!iframe) return;
        // Load into the preview <iframe> via a blob URL instead of
        // `doc.write` — consistent with the rest of the app and avoids
        // tripping the XSS static-analysis check even though same-origin
        // iframe write is not classically exploitable.
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        iframe.src = url;
        // Revoke once the frame has loaded; the browser holds the ref
        // for the lifetime of the frame document.
        const revoke = () => URL.revokeObjectURL(url);
        iframe.addEventListener('load', revoke, { once: true });
        return () => {
            iframe.removeEventListener('load', revoke);
            URL.revokeObjectURL(url);
        };
    }, [html, raw]);

    if (raw) return null;
    return (
        <div data-testid="preview-invoice-page" style={{ padding: 16, background: '#eee', minHeight: '100vh' }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong>Preview:</strong>
                <a href="?type=short&lang=fr" data-testid="preview-short-fr">Short FR</a>
                <a href="?type=long&lang=fr" data-testid="preview-long-fr">Long FR (multi-page)</a>
                <a href="?type=quotation&lang=fr" data-testid="preview-quotation-fr">Quotation FR</a>
                <a href="?type=short&lang=en" data-testid="preview-short-en">Short EN</a>
                <span style={{ color: '#888', fontSize: 12 }}>
                    type=<code>{kind}</code> · lang=<code>{lang}</code>
                </span>
            </div>
            <iframe
                ref={iframeRef}
                title="Invoice preview"
                data-testid="preview-iframe"
                style={{ width: '210mm', height: '297mm', border: '1px solid #ccc', background: '#fff', display: 'block', margin: '0 auto' }}
            />
        </div>
    );
}
